import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    apiSendHandoverEmail,
    apiGetHandoverEmailLog,
    apiListProjects,
    formatApiError,
} from "@/lib/api";
import { toast } from "sonner";
import { Mail, Plus, X, AlertCircle, Send, CheckCircle2, History } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SendEmailDialog({ open, onOpenChange, handover }) {
    const [projectDist, setProjectDist] = useState([]);
    const [emails, setEmails] = useState([]);
    const [draft, setDraft] = useState("");
    const [includeIncoming, setIncludeIncoming] = useState(true);
    const [includeDist, setIncludeDist] = useState(true);
    const [note, setNote] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const [log, setLog] = useState([]);

    useEffect(() => {
        if (!open) return;
        setEmails([]);
        setDraft("");
        setNote("");
        setError("");
        setSending(false);
        (async () => {
            try {
                const [projects, priorLog] = await Promise.all([
                    apiListProjects().catch(() => []),
                    apiGetHandoverEmailLog(handover.handover_id).catch(() => []),
                ]);
                const proj = projects.find(
                    (p) => p.project_id === handover.project_id,
                );
                setProjectDist(proj?.distribution_emails || []);
                setLog(priorLog || []);
            } catch (err) {
                setError(formatApiError(err));
            }
        })();
    }, [open, handover]);

    const canIncludeIncoming = !!handover?.incoming_user_id;

    function addEmail(raw) {
        const e = (raw || "").trim().toLowerCase();
        if (!e) return;
        if (!EMAIL_RE.test(e)) {
            setError(`"${e}" is not a valid email address`);
            return;
        }
        if (!emails.includes(e)) setEmails([...emails, e]);
        setDraft("");
        setError("");
    }
    function removeEmail(e) {
        setEmails(emails.filter((x) => x !== e));
    }

    const previewCount = useMemo(() => {
        const set = new Set(emails.map((e) => e.toLowerCase()));
        if (includeDist) projectDist.forEach((e) => set.add(e.toLowerCase()));
        if (includeIncoming && canIncludeIncoming) {
            // We don't have the incoming user's email in the payload; count as +1
            return set.size + 1;
        }
        return set.size;
    }, [emails, includeDist, includeIncoming, projectDist, canIncludeIncoming]);

    async function send() {
        setError("");
        if (previewCount === 0) {
            setError("Add at least one recipient.");
            return;
        }
        setSending(true);
        try {
            const res = await apiSendHandoverEmail(handover.handover_id, {
                to: emails,
                include_incoming: includeIncoming && canIncludeIncoming,
                include_distribution: includeDist,
                note: note || undefined,
            });
            toast.success(`Sent to ${res.log.recipients.length} recipient(s)`);
            setLog([res.log, ...log]);
            setEmails([]);
            setDraft("");
            setNote("");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSending(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto"
                data-testid="send-email-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-[#0072BC]" />
                        Send handover by email
                    </DialogTitle>
                    <DialogDescription>
                        Sends a branded HTML email with structured tables plus a PDF attachment, from the NightOwl address.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Quick options */}
                    <div className="space-y-2">
                        <div className="text-[11px] uppercase tracking-wider text-ntt-muted">
                            Quick recipients
                        </div>
                        <label
                            className={`flex items-center gap-3 border rounded-md px-3 h-11 bg-white ${canIncludeIncoming ? "border-ntt-border" : "border-ntt-border opacity-60"}`}
                        >
                            <Switch
                                checked={includeIncoming && canIncludeIncoming}
                                onCheckedChange={setIncludeIncoming}
                                disabled={!canIncludeIncoming}
                                data-testid="send-include-incoming"
                            />
                            <div className="text-[13px]">
                                <div className="font-medium text-[#070F26]">
                                    Send to incoming user
                                </div>
                                <div className="text-[11px] text-ntt-muted">
                                    {handover?.incoming_user_name ||
                                        "No incoming user set. Cannot include."}
                                </div>
                            </div>
                        </label>
                        <label className="flex items-center gap-3 border border-ntt-border rounded-md px-3 h-11 bg-white">
                            <Switch
                                checked={includeDist}
                                onCheckedChange={setIncludeDist}
                                data-testid="send-include-dist"
                            />
                            <div className="text-[13px] flex-1 min-w-0">
                                <div className="font-medium text-[#070F26]">
                                    Send to project distribution list
                                </div>
                                <div className="text-[11px] text-ntt-muted truncate">
                                    {projectDist.length === 0
                                        ? "No addresses saved. Add them in Projects screen."
                                        : `${projectDist.length} address(es) · ${projectDist.slice(0, 2).join(", ")}${projectDist.length > 2 ? ` +${projectDist.length - 2}` : ""}`}
                                </div>
                            </div>
                        </label>
                    </div>

                    {/* Free-form recipients */}
                    <div>
                        <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                            Additional recipients
                        </Label>
                        <div
                            className="flex flex-wrap gap-1.5 mb-2"
                            data-testid="send-email-chips"
                        >
                            {emails.length === 0 && (
                                <span className="text-[12px] text-ntt-muted italic">
                                    None yet.
                                </span>
                            )}
                            {emails.map((e) => (
                                <span
                                    key={e}
                                    data-testid={`send-email-chip-${e}`}
                                    className="inline-flex items-center gap-1 h-7 px-2 rounded-full text-[12px] bg-[#DBEAF6] text-[#0072BC]"
                                >
                                    {e}
                                    <button
                                        type="button"
                                        onClick={() => removeEmail(e)}
                                        className="hover:text-red-600"
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <Input
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === ",") {
                                        e.preventDefault();
                                        addEmail(draft);
                                    }
                                }}
                                onBlur={() => draft && addEmail(draft)}
                                placeholder="Type an email and press Enter"
                                data-testid="send-email-input"
                                className="h-10"
                                type="email"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => addEmail(draft)}
                                data-testid="send-email-add-btn"
                                className="h-10"
                            >
                                <Plus className="h-3.5 w-3.5 mr-1" />
                                Add
                            </Button>
                        </div>
                    </div>

                    <div>
                        <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                            Note (optional)
                        </Label>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Short message shown at the top of the email…"
                            data-testid="send-email-note"
                            className="w-full min-h-[64px] rounded-md border border-ntt-border p-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0072BC]/40"
                        />
                    </div>

                    <div
                        className="flex items-center justify-between text-[12px] text-ntt-muted bg-[#F4F6F9] rounded-md px-3 py-2"
                        data-testid="send-email-preview"
                    >
                        <span>Ready to send to {previewCount} recipient(s).</span>
                    </div>

                    {error && (
                        <div
                            data-testid="send-email-error"
                            className="flex items-start gap-2 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                        >
                            <AlertCircle className="h-4 w-4 mt-[1px] shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Send log */}
                    {log.length > 0 && (
                        <div className="border-t border-ntt-border pt-4">
                            <div className="text-[11px] uppercase tracking-wider text-ntt-muted mb-2 flex items-center gap-1">
                                <History className="h-3 w-3" />
                                Previously sent ({log.length})
                            </div>
                            <div
                                className="space-y-1.5 max-h-40 overflow-y-auto"
                                data-testid="send-email-log"
                            >
                                {log.map((l, idx) => (
                                    <div
                                        key={`${l.sent_at || "at"}-${idx}`}
                                        data-testid={`send-email-log-item-${idx}`}
                                        className="flex items-start gap-2 text-[12px] p-2 border border-ntt-border rounded-md bg-white"
                                    >
                                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-[2px] shrink-0" />
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[#070F26]">
                                                <span className="font-medium">
                                                    {l.sent_by_name || "Someone"}
                                                </span>{" "}
                                                to{" "}
                                                <span className="font-mono">
                                                    {l.recipients.join(", ")}
                                                </span>
                                            </div>
                                            <div className="text-[11px] text-ntt-muted">
                                                {fmtDT(l.sent_at)}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        data-testid="send-email-close-btn"
                    >
                        Close
                    </Button>
                    <Button
                        type="button"
                        onClick={send}
                        disabled={sending || previewCount === 0}
                        data-testid="send-email-send-btn"
                        className="bg-[#0072BC] hover:bg-[#005a94] text-white"
                    >
                        <Send className="h-3.5 w-3.5 mr-1.5" />
                        {sending ? "Sending…" : "Send email"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function fmtDT(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString(undefined, {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}
